import { type FC, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Action, Text } from '@soluto-private/mx-asurion-ui-react';

import { useAnalytics } from '@service-initiation/shared-data-access-analytics';
import { TranslationEntries } from '@service-initiation/tech-care-data-access-i18n';
import { useSetAlertBannerAtom } from '@service-initiation/tech-care-data-access-store';

import { GeolocationPositionError } from '../types';
import { type ZipCodeFormProps } from './ZipCodeForm';

const StoreVariantContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: start;
  align-items: start;
  margin-bottom: 2rem;
`;

const StoreVariantAction = styled(Action)`
  margin-left: 12px;
  margin-top: -8px;
`;

const TEXT = TranslationEntries.Repairs.ZipCode;

export const GeolocationButton: FC<ZipCodeFormProps> = (props) => {
  const {
    evaluateGeolocation,
    geolocationPositionError,
    showGeolocationPrompt,
  } = props;
  const { t } = useTranslation();
  const { dispatchViewEvent } = useAnalytics();
  const setAlertBanner = useSetAlertBannerAtom();
  const handleGeolocationPositionError = useCallback(() => {
    if (
      geolocationPositionError === GeolocationPositionError.PERMISSION_DENIED
    ) {
      setAlertBanner({
        bannerType: 'dark',
        message: t(TEXT.ZipCodeAlertBannerDeniedText),
      });
      dispatchViewEvent('AlertBanner', {
        message: t(TEXT.ZipCodeAlertBannerDeniedText),
      });
    }
  }, [dispatchViewEvent, geolocationPositionError, setAlertBanner, t]);

  const handleClick = useCallback(async () => {
    showGeolocationPrompt();
    handleGeolocationPositionError();
  }, [handleGeolocationPositionError, showGeolocationPrompt]);

  useEffect(() => {
    handleGeolocationPositionError();
  }, [handleGeolocationPositionError]);

  return (
    <StoreVariantContainer>
      <StoreVariantAction
        color="secondary"
        data-testid="geolocation-button"
        disabled={evaluateGeolocation}
        onClick={handleClick}
        variant="flat"
      >
        <Text color="black" size={1} weight="feather">
          {t(TEXT.GeoLocationButtonText)}
        </Text>
      </StoreVariantAction>
    </StoreVariantContainer>
  );
};
